import { useState, useEffect, useRef } from "react";
import { TextField, InputAdornment, IconButton } from "@material-ui/core";
import toast from "react-hot-toast";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import config from "../../config/config";

function Enter_Forgot({ formData, setFormStage }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [oldPassword, setOldPassword] = useState(""); // Added for current password
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Create a ref to hold the abort controller
  const abortControllerRef = useRef(null);
  // Create a ref to track mount status
  const isMounted = useRef(true);

  // Set up cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Set mounted flag to false
      isMounted.current = false;

      // Abort any pending fetch requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Safe state update function that only updates if component is mounted
  const safeSetState = (setter) => (...args) => {
    if (isMounted.current) {
      setter(...args);
    }
  };

  // Safe state setters
  const safeSetIsLoading = safeSetState(setIsLoading);
  const safeSetErrors = safeSetState(setErrors);

  const handleTogglePasswordVisibility = (field) => {
    if (field === "old") {
      setShowOldPassword(!showOldPassword);
    } else if (field === "new") {
      setShowNewPassword(!showNewPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateInputs = () => {
    const newErrors = { oldPassword: "", newPassword: "", confirmPassword: "" };
    let isValid = true;

    // Old password validation
    if (!oldPassword.trim()) {
      newErrors.oldPassword = "Current password is required";
      isValid = false;
    }

    // Password validation
    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required";
      isValid = false;
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters long";
      isValid = false;
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    safeSetErrors(newErrors);
    return isValid;
  };

  const resetPassword = async () => {
    console.log('resetPassword is called...')
    if (!validateInputs()) {
      return;
    }

    safeSetIsLoading(true);

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      // Prepare the request payload
      const payload = {
        OldPassword: oldPassword,
        NewPassword: newPassword,
        Email: formData.email || "", // Use email from formData
      };

      console.log("Submitting password change request:", payload);

      // Make the API call
      const response = await fetch(
        `${config.authService}admin/forgetPassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          signal,
        }
      );

      // Process the response
      const data = await response.json();
      console.log('payload : ', payload);
      console.log('resetPassword data : ', JSON.stringify(data));
      if (data.status !== 200) {
        throw new Error(data.message || "Failed to reset password");
      }

      // If successful
      if (isMounted.current) {
        toast.success("Password has been reset successfully");
        // Navigate back to login
        setFormStage(0);
      }
    } catch (err) {
      // Only handle error if it's not an abort error and component is mounted
      if (err.name !== "AbortError" && isMounted.current) {
        toast.error(err.message || "An error occurred while resetting your password");
        safeSetErrors((prev) => ({
          ...prev,
          oldPassword: "Invalid current password or server error",
        }));
        console.error("Password reset error:", err);
      }
    } finally {
      // Final state update with safety check
      safeSetIsLoading(false);
    }
  };

  const onKeyPressed = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      resetPassword();
    }
  };

  const buttonStyle = {
    width: "100%",
    padding: "12px 20px",
    fontSize: "15px",
    fontWeight: 500,
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#0055A4",
    color: "#ffffff",
  };

  const backButtonStyle = {
    background: "none",
    border: "none",
    color: "#ffffff",
    fontSize: "14px",
    cursor: "pointer",
    textDecoration: "underline",
    padding: "5px",
    fontWeight: 500,
    marginTop: "10px",
  };

  return (
    <div style={{ marginTop: "3em" }}>
      <h2
        style={{
          color: "#ffffff",
          marginBottom: "1.5rem",
          fontSize: "24px",
          fontWeight: "500",
          textAlign: "center",
        }}
      >
        Change Your Password
      </h2>

      <p
        style={{
          color: "#f0f0f0",
          marginBottom: "2rem",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        Please enter your current password and a new password or contact admin
      </p>

      {/* Current Password Field */}
      <div style={{ marginBottom: "1.5rem" }}>
        <TextField
          style={{
            backgroundColor: "#fff",
            width: "100%",
            borderRadius: "6px",
            minWidth: 80,
            fontSize: "17px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
          error={!!errors.oldPassword}
          variant="outlined"
          placeholder="Current Password"
          name="oldPassword"
          type={showOldPassword ? "text" : "password"}
          value={oldPassword}
          autoComplete="current-password"
          onKeyDown={onKeyPressed}
          onChange={(e) => {
            setOldPassword(e.target.value);
            if (errors.oldPassword) {
              setErrors((prev) => ({ ...prev, oldPassword: "" }));
            }
          }}
          helperText={errors.oldPassword}
          InputProps={{
            style: {
              paddingRight: "8px",
              fontFamily: "'Segoe UI', Arial, sans-serif",
              color: "#333",
              height: "48px",
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleTogglePasswordVisibility("old")}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  style={{ padding: "8px", marginRight: "0" }}
                >
                  {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* New Password Field */}
      <div style={{ marginBottom: "1.5rem" }}>
        <TextField
          style={{
            backgroundColor: "#fff",
            width: "100%",
            borderRadius: "6px",
            minWidth: 80,
            fontSize: "17px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
          error={!!errors.newPassword}
          variant="outlined"
          placeholder="New Password"
          name="newPassword"
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          autoComplete="new-password"
          onKeyDown={onKeyPressed}
          onChange={(e) => {
            setNewPassword(e.target.value);
            if (errors.newPassword) {
              setErrors((prev) => ({ ...prev, newPassword: "" }));
            }
          }}
          helperText={errors.newPassword}
          InputProps={{
            style: {
              paddingRight: "8px",
              fontFamily: "'Segoe UI', Arial, sans-serif",
              color: "#333",
              height: "48px",
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleTogglePasswordVisibility("new")}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  style={{ padding: "8px", marginRight: "0" }}
                >
                  {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* Confirm Password Field */}
      <div style={{ marginBottom: "1.5rem" }}>
        <TextField
          style={{
            backgroundColor: "#fff",
            width: "100%",
            borderRadius: "6px",
            minWidth: 80,
            fontSize: "17px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
          error={!!errors.confirmPassword}
          variant="outlined"
          placeholder="Confirm Password"
          name="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          autoComplete="new-password"
          onKeyDown={onKeyPressed}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (errors.confirmPassword) {
              setErrors((prev) => ({ ...prev, confirmPassword: "" }));
            }
          }}
          helperText={errors.confirmPassword}
          InputProps={{
            style: {
              paddingRight: "8px",
              fontFamily: "'Segoe UI', Arial, sans-serif",
              color: "#333",
              height: "48px",
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleTogglePasswordVisibility("confirm")}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  style={{ padding: "8px", marginRight: "0" }}
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div style={{ paddingTop: "20px", marginBottom: "15px" }}>
        {isLoading ? (
          <button style={buttonStyle} disabled>
            <span style={{ marginRight: "10px" }}>Processing</span>
            <div
              className="spinner"
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "50%",
                borderTopColor: "#ffffff",
                animation: "spin 1s linear infinite",
              }}
            ></div>
          </button>
        ) : (
          <button
            style={buttonStyle}
            onClick={resetPassword}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#004183";
              e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#0055A4";
              e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
            }}
          >
            Change Password
          </button>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <button
          style={backButtonStyle}
          onClick={() => setFormStage(0)}
          onMouseOver={(e) => {
            e.currentTarget.style.color = "#fdd34d";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = "#ffffff";
          }}
        >
          Back to Login
        </button>
      </div>

      <style jsx>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default Enter_Forgot;